import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import { ChurchAssetUploadTarget } from '@interfaces/church.interfaces';
import { TrainingModule, TrainingModuleAttributes, TrainingResource, TrainingResourceAttributes, TrainingVolunteerAssignment, TrainingVolunteerAssignmentBundle } from '@interfaces/training.interface';

/// Clients PUT the file to `url`, unlike the S3 image flow which POSTs a form.
export interface VideoUploadTarget {
  method: string;
  url: string;
  uploadID: string;
  timeoutSeconds: number;
}

export interface TrainingModuleDetail {
  module: TrainingModule;
  resources: TrainingResource[];
}

// The asset type the upload-target endpoint expects for Volunteering assets,
// alongside Launchpad's 'launchpad'.
const VOLUNTEERING_ASSET_TYPE = 'volunteering';

export default class TrainingController {
  private static baseURL: string = process.env.REACT_APP_SANCTUARY_API_URL || "";

  static async fetchTrainingModules(churchID: string, auth: AuthContextType): Promise<HTTPResponse<{ results: TrainingModule[] }>> {
    return await NetworkController.performRequestAt<{ results: TrainingModule[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/volunteering/training-modules`),
      auth.consumeRefreshToken
    );
  }

  static async fetchTrainingModule(
    churchID: string,
    moduleID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TrainingModuleDetail }>> {
    return await NetworkController.performRequestAt<{ result: TrainingModuleDetail }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/volunteering/training-modules/${moduleID}`),
      auth.consumeRefreshToken
    );
  }

  static async createTrainingModule(
    churchID: string,
    attributes: TrainingModuleAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TrainingModule }>> {
    return await NetworkController.performRequestAt<{ result: TrainingModule }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/volunteering/training-modules`, {
        attributes
      }),
      auth.consumeRefreshToken
    );
  }

  static async updateTrainingModule(
    churchID: string,
    moduleID: string,
    attributes: TrainingModuleAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TrainingModule }>> {
    return await NetworkController.performRequestAt<{ result: TrainingModule }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.PATCH, `/v1/church/${churchID}/volunteering/training-modules/${moduleID}`, {
        attributes
      }),
      auth.consumeRefreshToken
    );
  }

  static async createTrainingResource(
    churchID: string,
    moduleID: string,
    attributes: TrainingResourceAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TrainingResource }>> {
    return await NetworkController.performRequestAt<{ result: TrainingResource }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/volunteering/training-modules/${moduleID}/resources`, {
        attributes
      }),
      auth.consumeRefreshToken
    );
  }

  static async updateTrainingResource(
    churchID: string,
    moduleID: string,
    resourceID: string,
    attributes: TrainingResourceAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TrainingResource }>> {
    return await NetworkController.performRequestAt<{ result: TrainingResource }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PATCH,
        `/v1/church/${churchID}/volunteering/training-modules/${moduleID}/resources/${resourceID}`,
        { attributes }
      ),
      auth.consumeRefreshToken
    );
  }

  static async createVideoUploadTarget(
    churchID: string,
    moduleID: string,
    resourceID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<VideoUploadTarget>> {
    return await NetworkController.performRequestAt<VideoUploadTarget>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/volunteering/training-modules/${moduleID}/resources/${resourceID}/video-upload-target`
      ),
      auth.consumeRefreshToken
    );
  }

  // External videos are only playable through a URL the server hands back.
  static async fetchVideoURL(
    churchID: string,
    moduleID: string,
    resourceID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<unknown>> {
    return await NetworkController.performRequestAt<unknown>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/volunteering/training-modules/${moduleID}/resources/${resourceID}/video-url`
      ),
      auth.consumeRefreshToken
    );
  }

  // Assignments hang off the person rather than a team: a volunteer's training
  // can come from any team they're on.
  static async fetchVolunteerTrainingAssignments(
    churchID: string,
    personID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ results: TrainingVolunteerAssignmentBundle[] }>> {
    return await NetworkController.performRequestAt<{ results: TrainingVolunteerAssignmentBundle[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/people/${personID}/training-assignments`),
      auth.consumeRefreshToken
    );
  }

  static async assignTrainingModule(
    churchID: string,
    teamID: string,
    personID: string,
    assignment: {
      moduleID: string;
      /// ISO 8601, matching how dates are sent elsewhere in this API.
      dueDate: string;
      notifyStaffAssignerOnCompletion: boolean;
    },
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TrainingVolunteerAssignment }>> {
    return await NetworkController.performRequestAt<{ result: TrainingVolunteerAssignment }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/members/${personID}/training-assignments`,
        assignment
      ),
      auth.consumeRefreshToken
    );
  }

  static async createThumbnailUploadTarget(
    churchID: string,
    contentType: string,
    fileSizeBytes: number,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchAssetUploadTarget>> {
    return await NetworkController.performRequestAt<ChurchAssetUploadTarget>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/assets/upload-target`, {
        assetType: VOLUNTEERING_ASSET_TYPE,
        contentType,
        fileSizeBytes
      }),
      auth.consumeRefreshToken
    );
  }
}
